/**
 * `client-form-closure` (crossmarc/steward-app#2550, crossmarc/appcore#910; authorised by Matt
 * 2026-10-24): the firm's record that it has CLOSED one of a client's two forms, the intake
 * snapshot or the onboarding form, in the firm's own workspace. While the row exists the client
 * cannot change that form; deleting it reopens the form.
 *
 * ## Who reads it, and how
 *
 * - **appcore's onboarding gate** (`resolveOnboardingClientAuthority`, appcore
 *   `packages/core/src/db/client-form-closure.ts`) refuses every client write to the onboarding
 *   form with 409 `ONBOARDING_FORM_CLOSED_BY_FIRM` and reports `formClosed` on the read.
 * - **The intake writer** asks `GET /system/w/{ws}/client-form-closures/snapshot/{clientId}`
 *   before it changes a snapshot draft, and refuses the change (`form_closed`) when it is closed.
 *   appcore does not gate snapshot drafts itself, so that refusal is the only enforcement.
 * - **The façade** lists the rows for the firm's client list (the "Closed" stage) and creates or
 *   deletes them for Close form / Reopen form, under the caller's own bearer token.
 *
 * ## 🚨 `closureKey` MUST stay `unique: true`
 *
 * Both appcore readers find a closure by a deterministic key, never a list: `closureKey` is
 * `<form>.<clientId>` ({@link clientFormClosureKey}), and appcore writes the uniqueness slot
 * `UNIQ#client-form-closure#closureKey#<form>.<clientId>` ONLY because this type declares the
 * field `unique`. Drop the flag and every new closure has no slot, and both readers answer OPEN
 * without a log line, because "no slot" is indistinguishable from "no closure". The flag also
 * makes a second Close of the same form a 409 at appcore rather than a duplicate row.
 * `clientFormClosure.test.ts` pins the flag and the key format; appcore cannot check either
 * (the type is ours), and its reader's docblock points here.
 *
 * ## Shape
 *
 * Strict, every field a flat string (#1859, `packTypeDefs.test.ts`), `strict` audit: who closed
 * a client's form, and when, is a record to keep. Same `accessControl` as `onboarding-mark` and
 * `client-archive` (`STEWARD_WRITE_CAPABILITY`): no new capability, role or actor rule.
 *
 * ## Deleted by a permanent delete
 *
 * It declares `clientId`, so `clientPurgeManifest.test.ts` requires the permanent delete to reach
 * it (`services/clientPurge.ts` `purgeClientFormClosureRows`).
 */
import { z } from 'zod';

export const CLIENT_FORM_CLOSURE_TYPE_CODE = 'client-form-closure';

/** The two forms a firm can close. Mirrors appcore's `CLIENT_FORM_CLOSURE_FORMS`. */
export const CLIENT_FORM_CLOSURE_FORMS = ['snapshot', 'onboarding'] as const;
export type ClientFormClosureForm = typeof CLIENT_FORM_CLOSURE_FORMS[number];

export const isClientFormClosureForm = (value: unknown): value is ClientFormClosureForm =>
  typeof value === 'string' && (CLIENT_FORM_CLOSURE_FORMS as readonly string[]).includes(value);

/**
 * The `closureKey` value for (form, clientId). Byte-for-byte the key appcore's readers build
 * (`clientFormClosureKey` in appcore core): change one and every closure reads as open.
 */
export const clientFormClosureKey = (form: ClientFormClosureForm, clientId: string): string =>
  `${form}.${clientId}`;

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();
/** Cleaned at the façade (`safeDisplayText`) before it is stored. */
const displayText = z.string().min(1).max(200);

/** The STORED row. Every field is a flat string (#1859). */
export const ClientFormClosureAttributesSchema = z.strictObject({
  firmId: id,
  /** The `advisor-client` row this closure belongs to (that type's own appcore `instanceId`). */
  clientId: id,
  form: z.enum(CLIENT_FORM_CLOSURE_FORMS),
  /** {@link clientFormClosureKey}(form, clientId). The unique field appcore's readers key on. */
  closureKey: id,
  closedAt: instant,
  closedByUserId: id,
  closedByName: displayText,
}).refine((a) => a.closureKey === clientFormClosureKey(a.form, a.clientId), {
  message: 'closureKey must be <form>.<clientId>',
  path: ['closureKey'],
});

export type ClientFormClosureAttributes = z.infer<typeof ClientFormClosureAttributesSchema>;

/** Reads a stored `client-form-closure` row. `unreadable` covers "does not parse" and "is missing". */
export function readClientFormClosure(
  value: unknown,
): { kind: 'closure'; closure: ClientFormClosureAttributes } | { kind: 'unreadable' } {
  const parsed = ClientFormClosureAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'closure', closure: parsed.data } : { kind: 'unreadable' };
}

/**
 * Every user-facing string for Close form / Reopen form (crossmarc/steward-app#2550). No em or
 * en dashes and no sentence ending in a preposition (CLAUDE.md copy rules):
 * `clientFormClosure.test.ts` checks every string. `clientNotice` is the coordinator's wording,
 * verbatim from the #2550 brief.
 */
export const CLIENT_FORM_CLOSURE_COPY = {
  /** What the client sees on a form the firm has closed. */
  clientNotice: (firm: string) => `${firm} has closed this form. Contact your advisor to make changes.`,
  /** The same, when the firm's name cannot be read. */
  clientNoticeNoFirm: 'Your advisor has closed this form. Contact your advisor to make changes.',
  formName: { snapshot: 'Financial snapshot', onboarding: 'Onboarding form' } as Record<ClientFormClosureForm, string>,
  close: 'Close form',
  reopen: 'Reopen form',
  closedOn: (date: string, name: string) => `Closed on ${date} by ${name}.`,
  open: 'Open. The client can still make changes.',
  /** A form the client has sent: it is read-only for them, so "open" would be untrue (docs/43). */
  sent: (date: string) => `Sent on ${date}. The client can no longer make changes.`,
  /** The onboarding form after its send: appcore refuses a second send but still takes answer saves (appcore docs/06 section on the answers POST, read 2026-11-01), so the client can still update their answers. */
  sentEditable: (date: string) => `Sent on ${date}. The client can still update their answers.`,
  closeHint: 'Closing a form stops the client from making changes. You can reopen it at any time.',
  /** Closing the snapshot also cancels its unaccepted invitations; closing onboarding does not. */
  closeHintSnapshot: 'Any snapshot invitation they have not accepted is cancelled, so send a new one if you reopen it.',
  notOnTeam: "Only this client's own advisor or a firm admin can close or reopen their forms.",
  error: "That didn't work. Please try again, or contact us if it keeps happening.",
  stage: 'Closed',
} as const;
